const PrivacyPage = () => {
  return (
    <div className="pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <div className="space-y-8">
          {/* Header Section - matches landing page legal style */}
          <header className="text-center mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-crimson">Legal</p>
            <h1 className="mt-3 font-heading text-4xl font-bold text-foreground sm:text-5xl">
              Privacy Policy
            </h1>
            <p className="mt-4 text-muted-foreground">
              Last Updated: February 20, 2025
            </p>
          </header>

          <div className="prose dark:prose-invert max-w-none space-y-12">
            {/* Introduction */}
            <div className="space-y-6">
              <div className="rounded-xl border border-border bg-card p-6">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  At Cold Case Game Kits, a division of DGF Digital Inc. ("we," "us," or "our"), 
                  we are committed to protecting your privacy. This Privacy Policy explains how we collect, 
                  use, disclose, and safeguard your information when you visit our website 
                  coldcasegamekits.com or use our services.
                </p>
              </div>

              {/* Quick Reference Box - matches landing card style */}
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Quick Reference</h3>
                <div className="grid gap-3 text-sm text-muted-foreground">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Controller:</span>
                  <span className="md:col-span-2">DGF Digital Inc.</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Email:</span>
                  <span className="md:col-span-2">contact@dgfdigital.com</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Address:</span>
                  <span className="md:col-span-2">9-2020 Lanthier Drive Suite 111, Orleans, ON K4A 3V4</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <span className="font-medium">Effective Date:</span>
                  <span className="md:col-span-2">January 1, 2025</span>
                </div>
              </div>
            </div>
          </div>

          {/* Table of Contents - matches landing card style */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="font-heading text-xl font-semibold text-foreground mb-4">Table of Contents</h2>
            <nav className="grid gap-2 text-sm">
              <a href="#information-collect" className="text-crimson hover:underline">1. Information We Collect</a>
              <a href="#how-collect" className="text-crimson hover:underline">2. How We Collect Information</a>
              <a href="#use-information" className="text-crimson hover:underline">3. Use of Your Information</a>
              <a href="#data-retention" className="text-crimson hover:underline">4. Data Retention</a>
              <a href="#privacy-rights" className="text-crimson hover:underline">5. Your Privacy Rights</a>
              <a href="#data-security" className="text-crimson hover:underline">6. Data Security</a>
              <a href="#third-party" className="text-crimson hover:underline">7. Third-Party Services</a>
              <a href="#children" className="text-crimson hover:underline">8. Children's Privacy</a>
              <a href="#international" className="text-crimson hover:underline">9. International Data Transfers</a>
              <a href="#cookies" className="text-crimson hover:underline">10. Cookie Policy</a>
              <a href="#changes" className="text-crimson hover:underline">11. Changes to This Policy</a>
              <a href="#contact" className="text-crimson hover:underline">12. Contact Information</a>
              <a href="#authority" className="text-crimson hover:underline">13. Supervisory Authority</a>
            </nav>
          </div>

          {/* Main Content Sections - section headings and cards match landing */}
          <section id="information-collect" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">1. Information We Collect</h2>
            </div>

            <div className="pl-4 space-y-6">
              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3">1.1 Personal Information</h3>
                <div className="rounded-xl border border-border bg-card p-6">
                  <ul className="grid gap-2 list-none pl-0 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="font-medium text-foreground min-w-[140px]">Identity Data:</span>
                      <span>name, username, title</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-medium min-w-[140px]">Contact Data:</span>
                      <span>email address</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-medium min-w-[140px]">Financial Data:</span>
                      <span>payment information (processed by FastSpring)</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-medium min-w-[140px]">Account Data:</span>
                      <span>login credentials (if you use Clerk), purchase history</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-medium min-w-[140px]">Technical Data:</span>
                      <span>IP address, browser type, device information</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-medium min-w-[140px]">Usage Data:</span>
                      <span>website interaction</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3">1.2 Non-Personal Information</h3>
                <div className="rounded-xl border border-border bg-card p-6">
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Anonymous usage patterns</li>
                    <li>General demographic information</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section id="how-collect" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">2. How We Collect Information</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">2.1 Direct Collection</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Account registration</li>
                  <li>Game kit purchases</li>
                  <li>Customer support interactions</li>
                  <li>Newsletter subscriptions</li>
                  <li>Survey responses</li>
                </ul>
              </div>

              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">2.2 Automated Collection</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Cookies and similar technologies</li>
                  <li>Log files</li>
                  <li>Game kit analytics</li>
                  <li>Website usage tracking</li>
                </ul>
              </div>

              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">2.3 Third-Party Sources</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Authentication services (Clerk)</li>
                  <li>Payment processors (FastSpring)</li>
                  <li>Analytics providers (Google Analytics)</li>
                  <li>Marketing partners (Google Ads)</li>
                </ul>
              </div>
            </div>
          </section>

          <section id="use-information" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">3. Use of Your Information</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3">3.1 Business Purposes</h3>
                <div className="rounded-xl border border-border bg-card p-6">
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Providing and maintaining our services</li>
                    <li>Processing purchases and payments</li>
                    <li>Managing your account and preferences</li>
                    <li>Improving our products and services</li>
                    <li>Communicating with you about updates and offers</li>
                    <li>Preventing fraud and ensuring security</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3">3.2 Legal Basis (GDPR)</h3>
                <div className="rounded-xl border border-border bg-card p-6">
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Contract performance</li>
                    <li>Legal obligations</li>
                    <li>Legitimate interests</li>
                    <li>Consent (where required)</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section id="data-retention" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">4. Data Retention</h2>
            </div>

            <div className="space-y-6">
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Retention Purposes</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Provide our services</li>
                  <li>Maintain your account</li>
                  <li>Comply with legal obligations</li>
                  <li>Resolve disputes</li>
                  <li>Enforce agreements</li>
                </ul>
              </div>

              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Retention Periods</h3>
                <div className="grid gap-4">
                  <div className="flex justify-between border-b pb-2">
                    <span>Account information</span>
                    <span className="font-medium">Duration of account plus 2 years</span>
                  </div>
                  <div className="flex justify-between border-b pb-2">
                    <span>Purchase records</span>
                    <span className="font-medium">7 years (tax requirements)</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Communication records</span>
                    <span className="font-medium">3 years</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="privacy-rights" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">5. Your Privacy Rights</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3">5.1 GDPR Rights (EU/UK Users)</h3>
                <div className="rounded-xl border border-border bg-card p-6">
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Access your personal data</li>
                    <li>Correct inaccurate data</li>
                    <li>Delete your data</li>
                    <li>Restrict or object to processing</li>
                    <li>Data portability</li>
                    <li>Withdraw consent</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3">5.2 CCPA Rights (California Users)</h3>
                <div className="rounded-xl border border-border bg-card p-6">
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Know what personal information is collected</li>
                    <li>Know if personal information is sold or disclosed</li>
                    <li>Say no to the sale of personal information</li>
                    <li>Access your personal information</li>
                    <li>Request deletion</li>
                    <li>Equal service and price</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section id="data-security" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">6. Data Security</h2>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                We implement appropriate technical and organizational security
                measures:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>SSL/TLS encryption</li>
                <li>Secure data storage</li>
                <li>Access controls</li>
                <li>Regular security assessments</li>
                <li>Employee training</li>
                <li>Incident response procedures</li>
              </ul>
            </div>
          </section>

          <section id="third-party" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">7. Third-Party Services</h2>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>We use trusted third-party services for:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Authentication (Clerk)</li>
                <li>Payment processing (FastSpring)</li>
                <li>Analytics (Google Analytics)</li>
                <li>Customer support</li>
                <li>Email communication</li>
              </ul>
              <p>
                Each third-party service has its own privacy policy and data handling
                practices.
              </p>
            </div>
          </section>

          <section id="children" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">8. Children's Privacy</h2>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                Our services are not intended for children under 13. We do not
                knowingly collect or maintain information from children under 13. If
                we learn we have collected such information, we will delete it
                immediately.
              </p>
            </div>
          </section>

          <section id="international" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">9. International Data Transfers</h2>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                We may transfer your data to countries outside your residence. We
                ensure appropriate safeguards through:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Standard contractual clauses</li>
                <li>Privacy Shield certification</li>
                <li>Adequacy decisions</li>
                <li>Other legal mechanisms</li>
              </ul>
            </div>
          </section>

          <section id="cookies" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">10. Cookie Policy</h2>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>We use cookies and similar technologies to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Maintain your session</li>
                <li>Remember your preferences</li>
                <li>Analyze website usage</li>
                <li>Personalize content</li>
                <li>Improve user experience</li>
              </ul>
            </div>
          </section>

          <section id="changes" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">11. Changes to This Policy</h2>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                We may update this Privacy Policy periodically. We will notify you of
                any material changes through:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Email notification</li>
                <li>Website notice</li>
                <li>Account dashboard alert</li>
              </ul>
            </div>
          </section>

          <section id="contact" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">12. Contact Information</h2>
            </div>

            <div className="rounded-xl border border-crimson/20 bg-crimson/5 p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Data Protection Officer</h3>
              <address className="not-italic space-y-2 text-muted-foreground">
                <p className="font-medium text-foreground">DGF Digital Inc.</p>
                <p>9-2020 Lanthier Drive Suite 111</p>
                <p>Orleans, ON K4A 3V4</p>
                <p className="mt-4">
                  <span className="font-medium text-foreground">Email: </span>
                  <a href="mailto:contact@dgfdigital.com" className="text-crimson hover:underline">
                    contact@dgfdigital.com
                  </a>
                </p>
              </address>
            </div>
          </section>

          <section id="authority" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">13. Supervisory Authority</h2>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                You have the right to lodge a complaint with your local data
                protection authority if you are unhappy with how we have handled your
                personal data.
              </p>
            </div>
          </section>

          {/* Footer - matches landing legal footer */}
          <div className="border-t border-border pt-8 mt-12 text-center text-sm text-muted-foreground space-y-2">
            <p>Effective Date: January 1, 2025</p>
            <p>Last Updated: February 20, 2025</p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
